import moment from 'moment'

import t from '../../helpers/Languages.js'
import stopBundles from '../../../utils/stopBundles.js'
import {formatDistance, formatDuration} from '../../../utils/utils.js'
import {formatPrice} from '../../../utils/price.js'
import type {FrontEndRoute} from '../../../server/functions/route/route.types.js'
import type {Reseller} from '../../../server/functions/resellers/resellers.types.js'

// What a route card (RouteListItem), the route panel and the Routes table derive from a route of the OrderStore.
// Pure, so the Routes table, its CSV export and the cards show the same numbers.

export type RouteStatus = 'concept' | 'ready' | 'started' | 'completed'

export const routeStatusLabels: Record<RouteStatus, string> = {
    concept: 'Concept',
    ready: 'Gereed',
    started: 'Onderweg',
    completed: 'Afgerond'
}

type StatusRoute = Pick<FrontEndRoute, 'ready' | 'started' | 'completed'>

export function getRouteStatus(route: StatusRoute): RouteStatus {
    if (route.completed) {
        return 'completed'
    }

    if (route.started) {
        return 'started'
    }

    return route.ready ? 'ready' : 'concept'
}

/** 'Route verwijderen' in the menu of a route card: not once it is ready or a stop of it is completed */
export function canRemoveRoute(route: StatusRoute & Pick<FrontEndRoute, 'completedAddresses'>): boolean {
    return !(route.ready || route.started || route.completed || route.completedAddresses?.length)
}

/** The question before removing a route: a template route or one with stops, null when it can go without asking */
export function getRemoveRouteConfirm(route: Pick<FrontEndRoute, 'templateId' | 'addresses'>): {reason: 'template' | 'stops', title: string, text: string} | null {
    if (route.templateId) {
        return {reason: 'template', title: 'Template route verwijderen', text: 'Je gaat een route verwijderen die is aangemaakt vanuit route templates. Deze is niet meer terug te halen. Weet je zeker dat je door wil gaan?'}
    }

    if (route.addresses?.length) {
        return {reason: 'stops', title: 'Route verwijderen', text: 'Deze route bevat stops, weet je zeker dat je deze route wilt verwijderen?'}
    }

    return null
}

/** A collo with a barcode counts once, also when it is in the route twice (a pickup and a delivery) */
export function uniqueColli<T extends {barcode?: string}>(colli: T[] | undefined): T[] {
    return (colli || []).filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)
}

/** Start (actual, else planned) and end (actual, else estimated) of a route, as the moment strings they are stored in */
export function getRouteTimeRange(route: Pick<FrontEndRoute, 'startTime' | 'plannedStartTime' | 'date' | 'endTime' | 'estimatedEndTime'>): {start: string | undefined, end: string | undefined} {
    return {
        start: route.startTime || (route.plannedStartTime ? `${route.date} ${route.plannedStartTime}` : undefined),
        end: route.endTime || route.estimatedEndTime
    }
}

export function doRoutesOverlapInTime(route: FrontEndRoute, otherRoute: FrontEndRoute): boolean {
    const {start: thisRouteStart, end: thisRouteEnd} = getRouteTimeRange(route)
    const {start: startTime, end: endTime} = getRouteTimeRange(otherRoute)

    return moment(thisRouteStart).isBetween(moment(startTime), moment(endTime), 'minute', '[)') || moment(thisRouteEnd).isBetween(moment(startTime), moment(endTime), 'minute', '(]')
}

/**
 * Whether another open route of routes has this vehicle or messenger (id) at the same time as route, e.g. to mark the
 * options of the messenger and vehicle selects of a route card. Never for a completed route.
 */
export function doesRouteOverlapOn(route: FrontEndRoute, routes: FrontEndRoute[], field: 'vehicleId' | 'messengerId', id: string | undefined): boolean {
    return routes.some((r) => r[field] === id && r._id !== route._id && !r.completed && !route.completed && doRoutesOverlapInTime(route, r))
}

/** Another open route with the same vehicle or messenger at the same time (only messengers in users count) */
export function getRouteOverlaps(route: FrontEndRoute, routes: FrontEndRoute[], users: {_id?: string}[]): {routeOverlaps: boolean, messengerOverlaps: boolean} {
    const routeOverlaps = !!route.vehicleId && doesRouteOverlapOn(route, routes, 'vehicleId', route.vehicleId)

    const messengerExists = users.some((user) => user._id === route.messengerId)
    const messengerOverlaps = !!route.messengerId && messengerExists && doesRouteOverlapOn(route, routes, 'messengerId', route.messengerId)

    return {routeOverlaps, messengerOverlaps}
}

/**
 * Whether the (estimated) arrival at a stop is after or before its timeslot. Without an estimate the remaining
 * duration from now counts.
 */
export function getRouteTimeSlots(route: Pick<FrontEndRoute, 'addresses'>): {afterTimeSlot: boolean | undefined, beforeTimeSlot: boolean | undefined, inTimeSlots: boolean} {
    const afterTimeSlot = route.addresses?.some((address) => {
        let afterTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            afterTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        } else if (typeof address.duration === 'number') {
            afterTimeSlot = moment().add(address.duration || 0, 'seconds').isAfter(moment(address.endTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            afterTimeSlot = moment(address.timeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        }

        return afterTimeSlot
    })

    const beforeTimeSlot = !afterTimeSlot && route.addresses?.some((address) => {
        let beforeTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            beforeTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        } else if (typeof address.duration === 'number') {
            beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        }

        return beforeTimeSlot
    })

    return {afterTimeSlot, beforeTimeSlot, inTimeSlots: !afterTimeSlot && !beforeTimeSlot}
}

type PayloadColliType = Reseller['settings']['vehicleTypes'][0]['payloads'][0]['payloadColliType']

/** More colli (the colli type when a payload is per type), weight or distance than the vehicle type of the route allows */
export function getRouteLoadIssues(route: Pick<FrontEndRoute, 'colli' | 'vehicleTypeId' | 'distance'>, reseller: Pick<Reseller, 'settings'>): {tooMuchColli: PayloadColliType | boolean, tooHeavy: boolean, tooFar: boolean} {
    const colli = uniqueColli(route.colli)
    const amountOfColli = colli.length
    const vehicleType = reseller.settings.vehicleTypes.find((v) => v._id === route.vehicleTypeId)

    let tooMuchColli: PayloadColliType | boolean = false

    vehicleType?.payloads?.forEach((payload) => {
        if (tooMuchColli) {
            return
        }

        if (payload.payloadType === 'colli') {
            if (payload.payloadColliType) {
                const resellerColliType = reseller.settings.orders.colliTypes?.find((type) => type.id === payload.payloadColliType || type.name === payload.payloadColliType)
                const amountOfColliType = colli.filter((collo) => {
                    if (!collo?.type) {
                        return false
                    }

                    if (collo.type === payload.payloadColliType) {
                        return true
                    }

                    return !!(resellerColliType && (collo.type === resellerColliType.id || collo.type === resellerColliType.name))
                }).length

                if (payload.payload > 0 && amountOfColliType > payload.payload) {
                    tooMuchColli = payload.payloadColliType
                }
            } else if (payload.payload > 0 && amountOfColli > payload.payload) {
                tooMuchColli = true
            }
        }
    })

    let totalWeight = 0

    colli.forEach((collo) => {
        totalWeight += collo.weight
    })

    const tooHeavy = (vehicleType?.payloads || []).some((payload) => payload.payloadType === 'weight' && payload.payload > 0 && totalWeight > payload.payload)

    const tooFar = !!(vehicleType?.range && vehicleType.range > 0 && route.distance > vehicleType.range)

    return {tooMuchColli, tooHeavy, tooFar}
}

/** The warnings shown before a route is made ready */
export function getRouteReadyWarnings(issues: {tooMuchColli: unknown, tooHeavy: boolean, tooFar: boolean, outsideTimeSlots: boolean, routeOverlaps: boolean, messengerOverlaps: boolean}): string[] {
    const warnings: string[] = []

    if (issues.tooMuchColli) {
        warnings.push(t('routeReadyWarningTooMuchColli'))
    }

    if (issues.tooHeavy) {
        warnings.push(t('routeReadyWarningTooHeavy'))
    }

    if (issues.tooFar) {
        warnings.push(t('routeReadyWarningTooFar'))
    }

    if (issues.outsideTimeSlots) {
        warnings.push(t('routeReadyWarningNotInTimeSlots'))
    }

    if (issues.routeOverlaps) {
        warnings.push(t('routeReadyWarningVehicleOverlaps'))
    }

    if (issues.messengerOverlaps) {
        warnings.push(t('routeReadyWarningMessengerOverlaps'))
    }

    return warnings
}

/** The warnings of getRouteReadyWarnings for a route outside a route card, against the other routes of the store */
export function getRouteReadyWarningsFor(route: FrontEndRoute, reseller: Pick<Reseller, 'settings'>, routes: FrontEndRoute[], users: {_id?: string}[]): string[] {
    const {inTimeSlots} = getRouteTimeSlots(route)

    return getRouteReadyWarnings({
        ...getRouteLoadIssues(route, reseller),
        ...getRouteOverlaps(route, routes, users),
        outsideTimeSlots: !!((route.plannedStartTime || route.startTime) && !inTimeSlots)
    })
}

/** The duration of a completed route from its start and end (else the calculated one), and how much longer that took than planned */
export function getCompletedRouteDuration(route: Pick<FrontEndRoute, 'duration' | 'delay' | 'completed' | 'startTime' | 'endTime' | 'plannedDuration'>): {completedDuration: number, completedDelay: number} {
    let completedDuration = route.duration || 0
    let completedDelay = route.delay || 0

    if (route.completed && route.endTime && route.startTime) {
        completedDuration = moment(route.endTime).startOf('minute').diff(moment(route.startTime).startOf('minute'), 'seconds')
        completedDelay = completedDuration - route.plannedDuration
    }

    return {completedDuration, completedDelay}
}

/**
 * The numbers of a route card: the remaining stops, colli, distance (m) and duration (s) of an open route (all of
 * them before it started), the completed ones of a completed route.
 */
export function getRouteNumbers(route: FrontEndRoute): {stops: number, colli: number, distance: number, duration: number} {
    if (route.completed) {
        return {
            stops: route.completedAddresses?.length || 0,
            colli: uniqueColli(route.completedColli).length,
            distance: route.completedDistance || 0,
            duration: getCompletedRouteDuration(route).completedDuration
        }
    }

    return {
        stops: stopBundles.countStops(route.addresses || []),
        colli: uniqueColli(route.colli).length,
        distance: route.distance || 0,
        duration: route.duration || 0
    }
}

export interface RoutePanelTotals {
    nrOfRoutes: number
    nrOfStops: number
    nrOfColli: number
    totalDistance: number
    totalDuration: number
    lastEndTime: string
    totalSuccess: number
    totalNotCompleted: number
    totalTooLate: number
    totalDelay: number
}

/**
 * The totals at the bottom of the route list in the planner. Pass the routes shown in the list (date, tab and search
 * filter applied), so the totals never count routes of other days that are still in the OrderStore.
 */
export function getRoutePanelTotals(routes: FrontEndRoute[], activeRouteTab: 'open' | 'completed'): RoutePanelTotals {
    const totals: RoutePanelTotals = {nrOfRoutes: 0, nrOfStops: 0, nrOfColli: 0, totalDistance: 0, totalDuration: 0, lastEndTime: '', totalSuccess: 0, totalNotCompleted: 0, totalTooLate: 0, totalDelay: 0}

    for (const route of routes) {
        if (!route.completed && activeRouteTab === 'open') {
            totals.nrOfRoutes += 1
            totals.nrOfStops += stopBundles.countStops(route.addresses || [])
            totals.nrOfColli += route.colli?.length || 0
            totals.totalDistance += route.distance || 0
            totals.totalDuration += route.duration || 0

            if (route.estimatedEndTime && route.estimatedEndTime > totals.lastEndTime) {
                totals.lastEndTime = route.estimatedEndTime
            }
        }

        if (route.completed && activeRouteTab === 'completed') {
            totals.nrOfRoutes += 1
            totals.totalSuccess += route.success || 0
            totals.totalNotCompleted += route.notCompleted || 0
            totals.totalTooLate += route.tooLate || 0

            if (route.endTime && route.startTime) {
                const completedDuration = moment(route.endTime).diff(moment(route.startTime), 'seconds')
                totals.totalDuration += completedDuration

                if (route.plannedDuration) {
                    totals.totalDelay += completedDuration - route.plannedDuration
                }
            }
        }
    }

    return totals
}

/** A route as a row of the Routes table and its CSV export: the shown values, and the numbers to sort on */
export interface RouteRow {
    _id: string
    name: string
    /** YYYY-MM-DD */
    date: string
    status: string
    statusKey: RouteStatus
    messenger: string
    vehicle: string
    vehicleType: string
    branchName: string
    /** HH:mm, empty when unknown or not yet */
    startTimePlanned: string
    startTimeExecuted: string
    endTimePlanned: string
    endTimeExecuted: string
    stops: number
    colli: number
    // Planned, and executed once the route is completed (like the Carrier API), empty when unknown
    distancePlanned: string
    distancePlannedMeters: number
    distanceExecuted: string
    distanceExecutedMeters: number
    durationPlanned: string
    durationPlannedSeconds: number
    durationExecuted: string
    durationExecutedSeconds: number
    /** 12,50: the prices of the route's orders, per hour of the executed duration (else the planned one) */
    revenue: string
    revenueValue: number
    revenuePerHour: string
    revenuePerHourValue: number
}

/** A route of the OrderStore, which adds the revenue (see calculateRouteRevenue) */
type RevenueRoute = FrontEndRoute & {revenue?: number, revenuePerHour?: number}

interface RouteRowContext {
    reseller: Pick<Reseller, 'settings'>
    vehicles: {_id?: string, name?: string}[]
    usersById: Record<string, {name?: string} | undefined>
}

const formatTime = (time: string | undefined) => time && moment(time).isValid() ? moment(time).format('HH:mm') : ''

export function getRouteRow(route: RevenueRoute, {reseller, vehicles, usersById}: RouteRowContext): RouteRow {
    const status = getRouteStatus(route)
    const numbers = getRouteNumbers(route)
    const distanceExecuted = route.completed ? route.completedDistance || 0 : 0
    const durationExecuted = route.startTime && route.endTime ? moment(route.endTime).diff(moment(route.startTime), 'seconds') : 0

    const vehicle = vehicles.find((vehicle) => vehicle._id === route.vehicleId)
    const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === route.vehicleTypeId)

    return {
        _id: route._id,
        name: route.name || '',
        date: route.date,
        status: routeStatusLabels[status],
        statusKey: status,
        messenger: usersById[route.messengerId]?.name || route.messengerName || '',
        vehicle: vehicle?.name || vehicleType?.name || '',
        vehicleType: vehicleType?.name || '',
        branchName: reseller.settings.branches?.find((branch) => branch.id === route.branchId)?.name || '',
        startTimePlanned: route.plannedStartTime || '',
        startTimeExecuted: formatTime(route.startTime),
        endTimePlanned: formatTime(route.plannedEndTime),
        endTimeExecuted: formatTime(route.endTime),
        stops: numbers.stops,
        colli: numbers.colli,
        distancePlanned: route.plannedDistance ? formatDistance(route.plannedDistance) : '',
        distancePlannedMeters: route.plannedDistance || 0,
        distanceExecuted: distanceExecuted ? formatDistance(distanceExecuted) : '',
        distanceExecutedMeters: distanceExecuted,
        durationPlanned: route.plannedDuration ? formatDuration(route.plannedDuration) : '',
        durationPlannedSeconds: route.plannedDuration || 0,
        durationExecuted: durationExecuted ? formatDuration(durationExecuted) : '',
        durationExecutedSeconds: durationExecuted,
        revenue: formatPrice(route.revenue || 0),
        revenueValue: route.revenue || 0,
        revenuePerHour: formatPrice(route.revenuePerHour || 0),
        revenuePerHourValue: route.revenuePerHour || 0
    }
}

/** 'Starttijd (gepland)' */
export const plannedTitle = (title: string) => `${title} (${t('planned').toLowerCase()})`
export const executedTitle = (title: string) => `${title} (${t('executed').toLowerCase()})`

// Kilometres with a decimal comma, the export is opened in a Dutch spreadsheet (; separated)
const csvKm = (meters: number) => `${Math.round(meters / 100) / 10}`.replace('.', ',')

export interface RouteExportColumn {
    /** Stored in export templates (reseller.settings.routes.exportCsvTemplates), don't change */
    value: string
    label: string
    get: (row: RouteRow) => string | number
}

/** The columns a route export (template) can have, the branch only for accounts with branches */
export function getRouteExportColumns(withBranch: boolean): RouteExportColumn[] {
    return [
        {value: 'route.name', label: t('name'), get: (row) => row.name},
        {value: 'route.date', label: t('date'), get: (row) => moment(row.date).format('DD-MM-YYYY')},
        {value: 'route.status', label: t('status'), get: (row) => row.status},
        {value: 'route.messenger', label: t('messenger'), get: (row) => row.messenger},
        {value: 'route.vehicle', label: t('vehicle'), get: (row) => row.vehicle},
        {value: 'route.vehicleType', label: t('vehicleType'), get: (row) => row.vehicleType},
        ...withBranch ? [{value: 'route.branch', label: t('branch'), get: (row: RouteRow) => row.branchName}] : [],
        {value: 'route.startTimePlanned', label: plannedTitle(t('startTime')), get: (row) => row.startTimePlanned},
        {value: 'route.startTimeExecuted', label: executedTitle(t('startTime')), get: (row) => row.startTimeExecuted},
        {value: 'route.endTimePlanned', label: plannedTitle(t('endTime')), get: (row) => row.endTimePlanned},
        {value: 'route.endTimeExecuted', label: executedTitle(t('endTime')), get: (row) => row.endTimeExecuted},
        {value: 'route.stops', label: t('stops'), get: (row) => row.stops},
        {value: 'route.colli', label: t('colli'), get: (row) => row.colli},
        {value: 'route.distancePlanned', label: `${plannedTitle(t('distance'))} (km)`, get: (row) => row.distancePlannedMeters ? csvKm(row.distancePlannedMeters) : ''},
        {value: 'route.distanceExecuted', label: `${executedTitle(t('distance'))} (km)`, get: (row) => row.distanceExecutedMeters ? csvKm(row.distanceExecutedMeters) : ''},
        {value: 'route.durationPlanned', label: plannedTitle(t('duration')), get: (row) => row.durationPlanned},
        {value: 'route.durationExecuted', label: executedTitle(t('duration')), get: (row) => row.durationExecuted},
        {value: 'route.revenue', label: t('revenue'), get: (row) => row.revenue},
        {value: 'route.revenuePerHour', label: t('revenuePerHour'), get: (row) => row.revenuePerHour}
    ]
}
